import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ScanIcon } from '@assets/icons/scan.svg';

import DesignItem from '@/components/DesignItem';
import AddLocationModal from '@/features/design/components/AddLocationModal';
import LocationUpdateActionCard from '@/features/Scan/components/LocationUpdateActionCard';
import ScannedStylesListView from '@/features/Scan/components/ScannedStylesListView';
import { filterOutNotFoundItems, getViewTypeClass } from '@/features/Scan/utils';
import EmptyState from '@/features/trend-research/components/common/EmptyState';
import LocationScanActionCard from '@/features/Scan/components/LocationScanActionCard';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import ListViewType from '@/components/ListViewType';

const ScanCount = ({ itemCount, onClearAll, sampleCount, onSelectType }) => (
	<div className="total-and-clear-content d-flex justify-content-between align-items-center mb-12">
		<p className="regular-14 gray_dark_01">
			Style: <span className="weight-500 mr-1">{itemCount}</span> (Sample:{' '}
			<span className="weight-500">{sampleCount}</span>)
		</p>
		<div className="d-flex align-items-center justify-content-center">
			<p className="regular-14 gray_dark_01 cursor-pointer mr-3 text-underline" onClick={onClearAll}>
				Clear all
			</p>
			<div className="view-type-btm">
				<ListViewType type="SCAN_VIEW_TYPE" onSelectType={onSelectType} />
			</div>
		</div>
	</div>
);

const ScannedStyles = ({
	viewType,
	handleRemoveItem,
	handleClearAll,
	fetchAllTags,
	productWiseTags,
	updateProductTagOnSelect,
	onAddTag,
	onUndoLocationUpdate,
	onSuccessLocationScanFromStyleScan = () => {},
	handleLocationUpdateSuccessFromLocationScan = () => {},
	isScanWithCamera,
	onScanWithCamera,
	onSelectType
}) => {
	const [isShowLocationModal, setIsShowLocationModal] = useState(false);
	const [isShowLocationModalFromLocationScan, setIsShowLocationModalFromLocationScan] = useState(false);
	const [isShowTags, setIsShowTags] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		setIsMobile(window.innerWidth < 768);
	}, [window.innerWidth]);

	const {
		isUndo,
		scannedItems = [],
		locationUpdateResponse = {},
		locationResponseFromLocationScan = {}
	} = useSelector((state) => state.scanStore);
	const dispatch = useDispatch();

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};

	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				id: item?.id,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST'
			})
		);
	};

	const getUniqueStyleCount = () => {
		const uniqueIds = new Set(scannedItems.map((item) => item?.id));
		return uniqueIds.size;
	};

	const filteredScannedItems = filterOutNotFoundItems(scannedItems);

	const designItemProps = {
		fromPage: 'SCAN',
		isShowCardTop: false,
		fetchAllTags,
		productWiseTags,
		updateProductTagOnSelect,
		isShowAddTag: false,
		isShowAddTagButton: viewType === 'LARGE_VIEW',
		onClickAddTag: onAddTag,
		isShowRemoveButton: true,
		onRemoveItem: handleRemoveItem
	};

	const renderDesignItem = (item, index) => (
		<DesignItem
			design={item}
			index={index}
			key={`design_${index}`}
			{...designItemProps}
			onClickDesignImage={() => openDesignViewModal(item)}
		/>
	);

	const renderStylesByViewType = () => {
		switch (viewType) {
			case 'LIST_VIEW':
				return (
					<ScannedStylesListView
						styles={scannedItems}
						onRemoveItem={handleRemoveItem}
						removeAll={handleClearAll}
						onOpenDesignView={openDesignViewModal}
						onClickAddTag={onAddTag}
						productWiseTags={productWiseTags}
						fetchAllTags={fetchAllTags}
						updateProductTagOnSelect={updateProductTagOnSelect}
						setIsShowTags={setIsShowTags}
						isShowTags={isShowTags}
					/>
				);
			case 'LARGE_VIEW':
			case 'NARROW_VIEW':
			default:
				return filteredScannedItems.map((item, index) => renderDesignItem(item, index));
		}
	};

	const gridViewClass = viewType !== 'LIST_VIEW' ? 'scan-grid-and-img-view' : '';
	const viewTypeClass = getViewTypeClass(viewType);
	const isShowLocationUpdateCard = Object.keys(locationUpdateResponse).length > 0 && !isUndo;
	const isShowLocationScanCard = Object.keys(locationResponseFromLocationScan).length > 0;

	return (
		<>
			<ScanCount
				itemCount={getUniqueStyleCount()}
				onClearAll={handleClearAll}
				sampleCount={filteredScannedItems.length}
				onSelectType={onSelectType}
			/>
			{isShowLocationUpdateCard && (
				<LocationUpdateActionCard
					locationUpdateResponse={locationUpdateResponse}
					onUndoLocationUpdate={onUndoLocationUpdate}
					onShowLocationModal={setIsShowLocationModal}
				/>
			)}
			{isShowLocationScanCard && (
				<LocationScanActionCard
					locationResponseFromLocationScan={locationResponseFromLocationScan}
					onShowLocationModal={setIsShowLocationModalFromLocationScan}
				/>
			)}
			<div className={`${gridViewClass} ${viewTypeClass}`}>
				{scannedItems?.length > 0 ? (
					renderStylesByViewType()
				) : (
					<div className="d-flex flex-column align-items-center justify-content-center">
						<EmptyState title="No sample found. Scan your sample & location" />
						<div className="scan-with-camera d-flex justify-content-center ">
							{!isScanWithCamera && isMobile && (
								<button
									className="button size32 outline mr-2 border-0 bg-transparent"
									onClick={onScanWithCamera}
								>
									<ScanIcon className="mr-1" />
									<span className="gray_dark_01 text-underline">Scan with camera</span>
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			{isShowLocationModal && Object.keys(locationUpdateResponse).length > 0 && (
				<AddLocationModal
					onClose={() => setIsShowLocationModal(false)}
					selectdLocationToEdit={{
						...locationUpdateResponse,
						isEdit: locationUpdateResponse?.isFound
					}}
					setShowAddLocationModal={setIsShowLocationModal}
					onSuccess={onSuccessLocationScanFromStyleScan}
				/>
			)}
			{isShowLocationModalFromLocationScan &&
				Object.keys(locationResponseFromLocationScan).length > 0 && (
					<AddLocationModal
						onClose={() => setIsShowLocationModalFromLocationScan(false)}
						selectdLocationToEdit={{
							...locationResponseFromLocationScan?.locationResponse,
							isEdit: locationResponseFromLocationScan?.locationResponse?.isFound
						}}
						setShowAddLocationModal={setIsShowLocationModalFromLocationScan}
						onSuccess={handleLocationUpdateSuccessFromLocationScan}
					/>
				)}
		</>
	);
};

export default ScannedStyles;
